import { useEffect } from 'react';
import { handleOAuthSignIn, pollForGoogle } from './auth.utils';

const GOOGLE_CLIENT_IDS = {
  'si.com':
    '463477417652-hutbmku64c94svf03itqrno88ljmmpsc.apps.googleusercontent.com',
  'mentalfloss.com': '714792666811-lk0d9knqbktb95rt17kbdoc4074bmn85.apps.googleusercontent.com',
  // FanSided and FanSided network id
  default:
    '102936792104-jftdt1qtikefs9ac4barqnbt1g6b9f5i.apps.googleusercontent.com',
} as const;

const getClientId = (url: string): string => {
  if (url.includes('si.com')) {
    return GOOGLE_CLIENT_IDS['si.com'];
  }
  if (url.includes('www.mentalfloss.com')) {
    return GOOGLE_CLIENT_IDS['mentalfloss.com'];
  }
  return GOOGLE_CLIENT_IDS.default;
};

declare global {
  interface Window {
    isGoogleOAuthInitialized?: boolean;
    isGoogleOAuthPrompted?: boolean;
    googleOAuthPromptTimeoutId?: number;
    google?: any;
  }
}

const promptEvent = () => {
  window.dispatchEvent(
    new CustomEvent('google-oauth-modal', {
      detail: {
        name: 'prompt',
      },
    }),
  );
};

const closeEvent = () => {
  window.dispatchEvent(
    new CustomEvent('google-oauth-modal', {
      detail: {
        name: 'closed',
      },
    }),
  );
};

const controlEvent = () => {
  window.dispatchEvent(
    new CustomEvent('google-oauth-modal', {
      detail: {
        name: 'control',
      },
    }),
  );
};
interface GoogleOAuthProps {
  propertyUrl?: string;
  experiments?: string[];
  isMobile?: boolean;
}

export const GoogleOAuth = ({ propertyUrl, experiments = [], isMobile = false }: GoogleOAuthProps) => {
  useEffect(() => {
    const authCookies = ['mm_auth_session', 'mm_auth_dismissed'];
    if (!propertyUrl || authCookies.some(cookie => document.cookie.includes(cookie))) {
      return;
    }

    const isExperimentControl = experiments?.some(experiment => experiment.includes('control'));

    const PROMPT_SCROLL_MS = 15000;
    const PROMPT_SAFETY_MS = 90000;
    let promptTimer: number | undefined;
    let promptActive = false;

    function endPromptWindow(loggedIn = false) {
      promptActive = false;

      if (promptTimer) {
        clearTimeout(promptTimer);
      }

      if (!loggedIn) {
        window.google.accounts.id.cancel();
        document.cookie = 'mm_auth_dismissed=true; path=/;';
      }

      closeEvent();
    }

    // Schedule prompt 15s after the first user scroll
    const schedulePromptAfterDelay = () => {
      if (!window.isGoogleOAuthInitialized) return;
      if (window.isGoogleOAuthPrompted || window.googleOAuthPromptTimeoutId) return;

      window.googleOAuthPromptTimeoutId = window.setTimeout(async () => {
        if (isExperimentControl) {
          controlEvent();
          return;
        }

        promptActive = true;
        promptEvent();

        // Safety event: ensure closeEvent fires even if user doesn't actively close prompt
        promptTimer = window.setTimeout(() => {
          if (promptActive) {
            endPromptWindow(false);
          }
        }, PROMPT_SAFETY_MS);

        try {
          window.google.accounts.id.prompt((notification: any) => {
            const type = notification.getMomentType?.();
            if (type === 'dismissed' || type === 'skipped') {
              // Only close if prompt is still active (prevents double-close after sign-in)
              if (promptActive) {
                endPromptWindow(false);
              }
            }
          });
          window.isGoogleOAuthPrompted = true;
        } catch (e) {
          endPromptWindow(false);
          console.error('Failed to prompt Google OAuth:', e);
        }
      }, PROMPT_SCROLL_MS);
    };

    const initializeGoogle = async () => {
      try {
        await pollForGoogle();

        window.google.accounts.id.initialize({
          client_id: getClientId(propertyUrl),
          callback: (response: any) => handleOAuthSignIn('google', response),
          auto_select: false,
          cancel_on_tap_outside: false,
          itp_support: true,
        });
        window.isGoogleOAuthInitialized = true;
      } catch (error) {
        console.error('Failed to initialize Google OAuth:', error);
      }
    };

    if (isMobile) {
      if (!window.isGoogleOAuthInitialized) {
        initializeGoogle();
      }
      // One-time listener for the first scroll event
      window.addEventListener('scroll', schedulePromptAfterDelay, {
        once: true,
        passive: true,
      });
    }
  }, [propertyUrl, experiments, isMobile]);

  return null;
};
